import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, Card, CardHeader, CardContent, Typography, 
  Button, Breadcrumbs, Link, Stack, Chip, Paper, Divider
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { ArrowBack, TrendingUp, TrendingDown, Inventory, Timeline } from '@mui/icons-material';
import { DataService } from 'src/services/data-service';
import Loader from 'src/components/Loader';
import StatusCard from 'src/components/StatusCard';
import { ProductData, ProductAnalytics, ApiResponse } from 'src/types/Product-details.types';

const ProductAnalyticsPage: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState<ProductData | null>(null);
  const [analytics, setAnalytics] = useState<ProductAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Colors for charts
  const COLORS = ['#4A90E2', '#50C878', '#FFB347', '#FF6B6B', '#9B59B6', '#3498DB'];

  useEffect(() => {
    if (!productId) {
      navigate('/Units');
      return;
    }
    loadAnalyticsData();
  }, [productId, navigate]);

  const loadAnalyticsData = async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load basic product info
      const productResponse = await DataService.get(`api/product/${productId}/details`);
      const productResult: ApiResponse<any> = await productResponse.json();
      
      if (productResult.success && productResult.data) {
        setProduct(productResult.data.product);
      }
      
      // Load analytics data
      const analyticsResponse = await DataService.get(`api/product/${productId}/analytics`);
      const analyticsResult: ApiResponse<ProductAnalytics> = await analyticsResponse.json();
      
      if (analyticsResult.success && analyticsResult.data) {
        setAnalytics(analyticsResult.data);
      } else {
        throw new Error(analyticsResult.message || 'Failed to load analytics data');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Loader 
              type="gear"
              size="large"
              message="Loading Analytics..."
              subMessage="Analyzing product data and trends"
              showProgress={true}
            />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
        <Container maxWidth="xl">
          <StatusCard
            variant="error"
            title="Error Loading Analytics"
            message={error}
            actionLabel="Try Again"
            onAction={loadAnalyticsData}
            layout="page"
          />
        </Container>
      </Box>
    );
  }

  if (!product || !analytics) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
        <Container maxWidth="xl">
          <StatusCard
            variant="not-found"
            title="Analytics Not Available"
            message="Analytics data is not available for this product."
            actionLabel="Go Back"
            onAction={() => navigate(`/Units/${productId}`)}
            layout="page"
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="/prodUnitsucts">Units</Link>
          <Link color="inherit" href={`/Units/${productId}`}>{product.name}</Link>
          <Typography color="text.primary">Analytics</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
            color: 'white', 
            p: 3 
          }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate(`/Units/${productId}`)}
                sx={{ color: 'white', borderColor: 'white' }}
                variant="outlined"
              >
                Back to Product
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {analytics.velocityMetrics.averageDailyMovement.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Daily Movement
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}>
              <Timeline sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {analytics.velocityMetrics.turnoverRate.toFixed(2)}x
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Turnover Rate
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50', border: 1, borderColor: 'warning.200' }}>
              <Inventory sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {analytics.velocityMetrics.daysOfStock}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Days of Stock
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.50', border: 1, borderColor: 'info.200' }}>
              <TrendingDown sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {analytics.locationDistribution.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Storage Locations
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Movement Trends */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardHeader title="Movement Trends" subheader="Inbound vs Outbound movements over time" />
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={analytics.movementTrends.labels.map((label, index) => ({
                    date: label,
                    inbound: analytics.movementTrends.inbound[index],
                    outbound: analytics.movementTrends.outbound[index]
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="inbound" 
                      stroke="#4CAF50" 
                      strokeWidth={3}
                      name="Inbound"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="outbound" 
                      stroke="#F44336" 
                      strokeWidth={3}
                      name="Outbound"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Location Distribution */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardHeader title="Stock Distribution" subheader="Current stock by warehouse" />
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={analytics.locationDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ warehouseName, percentage }) => `${warehouseName}: ${percentage.toFixed(1)}%`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="quantity"
                    >
                      {analytics.locationDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} units`, 'Quantity']} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Seasonal Trends */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Seasonal Analysis" subheader="Monthly stock levels and movement patterns" />
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={analytics.seasonalTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="quantity"
                      stackId="1"
                      stroke="#4A90E2"
                      fill="#4A90E2"
                      fillOpacity={0.6}
                      name="Stock Quantity"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="movements"
                      stroke="#FF6B6B"
                      strokeWidth={3}
                      name="Monthly Movements"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Metrics */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Performance Metrics" />
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Average Daily Movement
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.velocityMetrics.averageDailyMovement.toFixed(2)} units/day
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Inventory Turnover Rate
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.velocityMetrics.turnoverRate.toFixed(2)}x per year
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.velocityMetrics.turnoverRate > 4 ? 'High velocity' : 
                       analytics.velocityMetrics.turnoverRate > 2 ? 'Medium velocity' : 'Low velocity'}
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Days of Stock Remaining
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.velocityMetrics.daysOfStock} days
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.velocityMetrics.daysOfStock < 30 ? 'Low stock warning' :
                       analytics.velocityMetrics.daysOfStock < 60 ? 'Normal stock level' : 'High stock level'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Location Analysis */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Location Analysis" />
              <CardContent>
                <Stack spacing={2}>
                  {analytics.locationDistribution.map((location, index) => (
                    <Paper key={location.warehouseName} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {location.warehouseName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {location.percentage.toFixed(1)}% of total stock
                          </Typography>
                        </Box>
                        <Chip
                          label={`${location.quantity} units`}
                          size="small"
                          sx={{ 
                            bgcolor: COLORS[index % COLORS.length], 
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Recommendations" />
              <CardContent>
                <Grid container spacing={2}>
                  {analytics.velocityMetrics.daysOfStock < 30 && (
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'warning.50', border: 1, borderColor: 'warning.200' }}>
                        <Typography variant="body2" fontWeight="bold" color="warning.dark">
                          ⚠️ Low Stock Alert
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Consider restocking soon. Current stock will last approximately {analytics.velocityMetrics.daysOfStock} days.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {analytics.velocityMetrics.turnoverRate < 1 && (
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'info.50', border: 1, borderColor: 'info.200' }}>
                        <Typography variant="body2" fontWeight="bold" color="info.dark">
                          📊 Slow Moving Item
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This product has low turnover. Consider promotional activities or review pricing strategy.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {analytics.locationDistribution.length > 5 && (
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'secondary.50', border: 1, borderColor: 'secondary.200' }}>
                        <Typography variant="body2" fontWeight="bold" color="secondary.dark">
                          🏗️ Optimize Distribution
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Stock is spread across many locations. Consider consolidating to reduce handling costs.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductAnalyticsPage;